import React, { useEffect, useRef, useState, type ForwardRefExoticComponent, type RefAttributes } from 'react';
import { Alert, Box, Card, CircularProgress, Fade, Stack, Typography } from '@mui/material';
import { Button } from 'components/index';
import { Edit, Refresh, Save } from '@mui/icons-material';
import { useMonitoringReportShared } from '../../context/MonitoringReportContext';
import HtmlReportEditor, { type HtmlReportEditorHandle } from '../../../../HtmlReportEditor';
import { usePrompt } from '../../../../AppPrompt';

type HtmlReportEditorRef = HtmlReportEditorHandle;

const HtmlReportEditorWithRef = HtmlReportEditor as ForwardRefExoticComponent<{ htmlContent: string } & RefAttributes<HtmlReportEditorRef>>;

interface ProgressState {
  isGenerating: boolean;
  message: string;
  error?: string;
}

type PreviewMode = 'pdf' | 'html-edit';

const PREVIEW_TRANSITION_MS = 360;

const PreviewStep = () => {
  const editorRef = useRef<HtmlReportEditorRef>(null);
  const { reportState, generatePDFPreview, clearPDFPreview, api, setReportState, reportType, navigationGuardRef, setExportHtml } =
    useMonitoringReportShared();
  const { getConfirmation } = usePrompt();
  const [progress, setProgress] = useState<ProgressState>({
    isGenerating: false,
    message: '',
  });
  const [previewMode, setPreviewMode] = useState<PreviewMode>('pdf');
  const [editableHtml, setEditableHtml] = useState<string>('');
  const [isSavingEditedPdf, setIsSavingEditedPdf] = useState(false);
  const [isPreparingEditor, setIsPreparingEditor] = useState(false);
  const preparationRequestIdRef = useRef(0);

  const hasUnsavedEdits = () => editorRef.current?.isDirty() ?? false;

  useEffect(() => {
    if (!editableHtml && !reportState.pdfPreview?.blobUrl && !reportState.pdfPreview?.isGenerating) {
      handleGeneratePreview();
    }
  }, []);

  useEffect(() => {
    return () => {
      clearPDFPreview();
    };
  }, []);

  const leaveEditorPrompt = {
    heading: 'Leave PDF Editor?',
    description: 'Your edits will be lost if you return to the main workflow. Would you like to proceed?',
    confirmButton: { label: 'Leave Anyway', color: 'error' as const },
    cancelButton: { label: 'Stay in Editor', color: 'primary' as const },
  };

  useEffect(() => {
    if (!navigationGuardRef) return;

    if (previewMode === 'html-edit') {
      // Wizard step guard
      navigationGuardRef.current = async () => {
        if (!hasUnsavedEdits()) return true;
        return getConfirmation(leaveEditorPrompt);
      };

      const handleBeforeUnload = (e: BeforeUnloadEvent) => {
        if (!hasUnsavedEdits()) return;
        e.preventDefault();
        e.returnValue = '';
      };

      // rrPushState = React Router's wrapped pushState (what was on window.history when we installed).
      // nativePushState = the real browser API, bypasses both our override and RR's wrapper.
      const rrPushState = window.history.pushState.bind(window.history);
      const nativePushState = History.prototype.pushState.bind(window.history);

      window.history.pushState = (...args) => {
        if (!hasUnsavedEdits()) {
          rrPushState(...args);
          return;
        }
        getConfirmation(leaveEditorPrompt).then(confirmed => {
          if (confirmed) {
            bypassNextPopState = true;
            nativePushState(...args);
            window.dispatchEvent(new PopStateEvent('popstate', { state: args[0] }));
          }
        });
      };

      let bypassNextPopState = false;
      const handlePopState = (e: PopStateEvent) => {
        if (bypassNextPopState) {
          bypassNextPopState = false;
          return;
        }
        if (!hasUnsavedEdits()) return;
        e.preventDefault();
        nativePushState(window.history.state, '');
        getConfirmation(leaveEditorPrompt).then(confirmed => {
          if (confirmed) {
            bypassNextPopState = true;
            window.history.back();
          }
        });
      };

      window.addEventListener('beforeunload', handleBeforeUnload);
      window.addEventListener('popstate', handlePopState);
      return () => {
        window.history.pushState = rrPushState;
        window.removeEventListener('beforeunload', handleBeforeUnload);
        window.removeEventListener('popstate', handlePopState);
        navigationGuardRef.current = null;
      };
    } else {
      navigationGuardRef.current = null;
    }
  }, [previewMode, navigationGuardRef, getConfirmation]);

  const ensureEditableHtml = async () => {
    if (editableHtml) return editableHtml;
    const requestId = ++preparationRequestIdRef.current;
    setIsPreparingEditor(true);
    setProgress({ isGenerating: true, message: 'Preparing Editor...' });
    try {
      const html = await api.buildReportHtml(reportState);
      if (preparationRequestIdRef.current !== requestId) return;
      setEditableHtml(html);
      return html;
    } finally {
      if (preparationRequestIdRef.current === requestId) {
        setIsPreparingEditor(false);
        setProgress(prev => ({ ...prev, isGenerating: false, message: '' }));
      }
    }
  };

  const handleGeneratePreview = async () => {
    preparationRequestIdRef.current++;
    setIsPreparingEditor(false);
    setEditableHtml('');
    setProgress({ isGenerating: true, message: 'Starting PDF generation...' });
    try {
      await generatePDFPreview((message: string) => {
        setProgress({ isGenerating: true, message });
      });
      setProgress({ isGenerating: false, message: 'PDF generated successfully!' });
    } catch (error) {
      setProgress({ isGenerating: false, message: '', error: error instanceof Error ? error.message : 'Failed to generate PDF' });
    }
  };

  const handleGeneratePdfFromEditedHtml = async (editedHtml?: string) => {
    setIsSavingEditedPdf(true);
    setProgress({ isGenerating: true, message: 'Generating PDF from edited content...', error: undefined });
    try {
      const fromIframe = editedHtml?.trim();
      const htmlToUse = fromIframe && fromIframe.length > 0 ? fromIframe : editableHtml || (await ensureEditableHtml()) || '';
      const { blob, sizeInfo } = await api.exportPdf(htmlToUse);
      // Export re-renders from this HTML, so the user's edits survive the
      // quality choice instead of being reverted to the generated report.
      setExportHtml(htmlToUse);
      const projTitle = reportState.projectSummary?.projectTitle || 'Report';
      const sanitizeFilename = (s: string) =>
        s
          .replace(/[\\/:*?"<>|]/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
      const formatFilenameDate = (dateStr: string | undefined | null) => {
        if (!dateStr) return '';
        const parts = dateStr.split('T')[0].split('-');
        return parts.length === 3 ? `${parts[1]}-${parts[2]}-${parts[0].slice(-2)}` : '';
      };
      const reportDate = formatFilenameDate(reportState.siteConditions?.reportDate || reportState.siteConditions?.inspectionDate);
      const reportNum = reportState.siteConditions?.reportNumber || '';
      const suffix = reportType === 'limited' ? ' LIMITED' : '';
      const fileName = `${sanitizeFilename(projTitle)}${reportDate ? ' ' + reportDate : ''} CDCC Report${reportNum ? ' ' + reportNum : ''}${suffix}.pdf`;
      const blobUrl = URL.createObjectURL(blob);
      const oldBlobUrl = reportState.pdfPreview?.blobUrl;
      setReportState((prev: any) => {
        return {
          ...prev,
          pdfPreview: {
            blobUrl,
            fileName,
            isGenerating: false,
            error: undefined,
            sizeInfo,
          },
        };
      });
      if (oldBlobUrl) {
        URL.revokeObjectURL(oldBlobUrl);
      }
      setEditableHtml(htmlToUse);
      setPreviewMode('pdf');
      setProgress({ isGenerating: false, message: 'PDF generated successfully!' });
    } catch (error) {
      setProgress({ isGenerating: false, message: '', error: error instanceof Error ? error.message : 'Failed to generate PDF' });
      // Stay in html-edit mode so the user can retry without losing their edits
    } finally {
      setIsSavingEditedPdf(false);
    }
  };

  const handleRetry = () => {
    handleGeneratePreview();
  };

  const openHtmlEditor = async () => {
    try {
      await ensureEditableHtml();
      setPreviewMode('html-edit');
    } catch (error) {
      setProgress({ isGenerating: false, message: '', error: error instanceof Error ? error.message : 'Failed to prepare HTML editor' });
      setReportState((prev: any) => {
        if (prev.pdfPreview?.blobUrl) URL.revokeObjectURL(prev.pdfPreview.blobUrl);
        return { ...prev, pdfPreview: { ...prev.pdfPreview, blobUrl: undefined } };
      });
    }
  };

  // Always render preview UI

  const showPdfOrEditor = previewMode === 'html-edit' || (previewMode === 'pdf' && !!reportState.pdfPreview?.blobUrl);
  const pdfLoading = reportState.pdfPreview?.isGenerating || progress.isGenerating;
  const pdfError = reportState.pdfPreview?.error || progress.error;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant='h6'>{previewMode === 'pdf' ? 'PDF Preview' : 'PDF Editor'}</Typography>
          <Stack direction='row' spacing={1}>
            {previewMode === 'pdf' ? (
              <>
                <Button
                  startIcon={<Edit />}
                  onClick={openHtmlEditor}
                  disabled={reportState.pdfPreview?.isGenerating || isPreparingEditor}
                  variant='contained'
                  size='small'
                  sx={{ bgcolor: '#0283a0', '&:hover': { bgcolor: '#026b82' } }}
                >
                  Edit
                </Button>
                <Button
                  startIcon={<Refresh />}
                  onClick={handleGeneratePreview}
                  loading={reportState.pdfPreview?.isGenerating || isPreparingEditor}
                  disabled={isPreparingEditor}
                  variant='text'
                  size='small'
                >
                  Refresh Preview
                </Button>
              </>
            ) : (
              <Stack direction='row' spacing={1} alignItems='center'>
                {pdfError ? (
                  <Alert
                    severity='error'
                    sx={{ py: 0 }}
                    onClose={() => {
                      setProgress(prev => ({ ...prev, error: undefined }));
                      setReportState((prev: any) => ({ ...prev, pdfPreview: { ...prev.pdfPreview, error: undefined } }));
                    }}
                  >
                    {pdfError}
                  </Alert>
                ) : null}
                <Button
                  startIcon={<Save />}
                  onClick={() => handleGeneratePdfFromEditedHtml(editorRef.current?.getEditedHtml())}
                  loading={isSavingEditedPdf}
                  variant='contained'
                  size='small'
                  sx={{ bgcolor: '#0283a0', '&:hover': { bgcolor: '#026b82' } }}
                >
                  Save & Generate PDF
                </Button>
              </Stack>
            )}
          </Stack>
        </Box>

        <Box
          sx={{
            flex: 1,
            p: 2,
            minHeight: 0,
            overflow: previewMode === 'pdf' ? 'auto' : 'hidden',
            position: 'relative',
          }}
        >
          {/* PDF ↔ HTML editor: stacked fades so exit/enter overlap instead of swapping instantly */}
          <Box
            sx={{
              position: 'relative',
              flex: 1,
              minHeight: 0,
              height: '100%',
              display: showPdfOrEditor ? 'block' : 'none',
            }}
          >
            <Fade in={previewMode === 'pdf' && !!reportState.pdfPreview?.blobUrl} timeout={PREVIEW_TRANSITION_MS} unmountOnExit>
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  minHeight: 0,
                  bgcolor: 'white',
                  borderRadius: 1,
                  overflow: 'auto',
                }}
              >
                {reportState.pdfPreview?.blobUrl ? (
                  <iframe
                    src={`${reportState.pdfPreview.blobUrl}#view=FitH`}
                    style={{
                      width: '100%',
                      height: '100%',
                      minHeight: 0,
                      border: 'none',
                      display: 'block',
                    }}
                    title='PDF Preview'
                  />
                ) : null}
              </Box>
            </Fade>
            <Fade in={previewMode === 'html-edit'} timeout={PREVIEW_TRANSITION_MS} unmountOnExit>
              <Box sx={{ position: 'absolute', inset: 0, minHeight: 0, overflow: 'hidden' }}>
                <HtmlReportEditorWithRef ref={editorRef} htmlContent={editableHtml} />
              </Box>
            </Fade>
          </Box>

          {previewMode === 'pdf' && !reportState.pdfPreview?.blobUrl && pdfLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', gap: 3 }}>
              <Typography variant='h6' color='primary'>
                Generating PDF Preview
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={60} />
                <Typography variant='body2' textAlign='center'>
                  {progress.message}
                </Typography>
              </Box>
            </Box>
          ) : null}
          {previewMode === 'pdf' && !reportState.pdfPreview?.blobUrl && !pdfLoading && pdfError ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', gap: 2 }}>
              <Alert severity='error' sx={{ maxWidth: 500 }}>
                <Typography variant='h6' gutterBottom>
                  Failed to Generate PDF
                </Typography>
                <Typography variant='body2' gutterBottom>
                  {pdfError}
                </Typography>
                <Button variant='contained' onClick={handleRetry} sx={{ mt: 1 }}>
                  Try Again
                </Button>
              </Alert>
            </Box>
          ) : null}
          {previewMode === 'pdf' && !reportState.pdfPreview?.blobUrl && !pdfLoading && !pdfError ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', gap: 2 }}>
              <Typography variant='h6' color='text.secondary'>
                No PDF Preview Available
              </Typography>
              <Typography variant='body2' color='text.secondary' textAlign='center'>
                Click &quot;Refresh Preview&quot; to generate a new PDF preview.
              </Typography>
            </Box>
          ) : null}
        </Box>
      </Card>
    </Box>
  );
};

export default PreviewStep;
